<template>
  <div class="rd-screen">
    <report-tools>
      <span class="rd-muted">{{ $t("report_desktop.ordered_taken_note") }}</span>
    </report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!packingCompanies.length"
      icon="mdi-shopping-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <section v-for="company in packingCompanies" v-else :key="company.key">
      <div class="rd-section">
        <h2>
          {{ company.name }}
          <span v-if="company.subtitle" class="rd-section__sub">{{ company.subtitle }}</span>
        </h2>
        <span class="rd-num">{{ company.taken }} / {{ company.ordered }}</span>
      </div>
      <ui-table
        variant="report"
        total-key="name"
        :columns="columns"
        :rows="company.rows"
        :row-key="(row) => row.key"
        :totals="companyTotals(company)"
        :total-label="$t('total')"
      >
        <template #cell-name="{ row }">
          {{ row.name }}
          <span v-if="row.combination" class="rd-muted rd-line">{{ row.combination }}</span>
        </template>

        <template #cell-total="{ value }"><b>{{ value }}</b></template>
      </ui-table>
    </section>
  </div>
</template>

<script>
import ReportAllCompaniesTabPacking from "@/components/report/all-companies/tabs/ReportAllCompaniesTabPacking.vue";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";

/**
 * "Sve firme – zbirni izveštaj → Pakovanje" na računaru: po firmi stavke sa "naručeno / preuzeto"
 * po smeni. Nasleđuje učitavanje, štampu i Excel iz ReportAllCompaniesTabPacking.
 */
export default {
  name: "ReportDesktopAllPacking",
  extends: ReportAllCompaniesTabPacking,
  mixins: [hasShiftSubtitle],
  components: { ReportState, ReportTools },
  props: {
    /** Smene stižu kroz selectedShifts (prop starog taba); ovde samo da se ne prikažu kao atribut. */
    shifts: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    /** Kolone tabele (iste za svaku firmu): redni broj, obrok, smene, ukupno. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("meal"), cellCls: "is-name" },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, label: this.$t(shift.label), align: "end", num: true, value: (row) => this.formatQuantity(row.source, shift.key) })),
        { key: "total", label: this.$t("total"), align: "end", num: true, value: (row) => this.formatQuantity(row.source, "total") },
      ];
    },
    /** Smene 1–3 uvek, četvrta samo kad neka stavka ima obroke u njoj. */
    visibleShifts() {
      return this.selectedShiftColumns.filter(
        (shift) =>
          shift.id < 4 ||
          this.visibleCompanies.some((company) => company.items.some((item) => item[shift.key] > 0))
      );
    },
    packingCompanies() {
      const search = this.filters.search;
      return this.visibleCompanies
        .map((company) => {
          const companyMatches = !search || this.matches(company, ["name"], search);
          const rows = company.items
            .filter((item) => companyMatches || this.matches(item, ["name", "combination"], search))
            .map((item) => ({
              key: `${item.id}-${item.combination || ""}`,
              name: this.toLatin(item.name),
              combination: item.combination ? this.toLatin(item.combination) : "",
              source: item,
            }));
          return {
            key: company.id || company.name,
            name: company.name,
            subtitle: [company.address, company.city].filter(Boolean).join(", "),
            rows,
            totals: company.totals,
            ordered: company.totals.total,
            taken: company.totals.total_payed,
          };
        })
        .filter((company) => company.rows.length);
    },
    summary() {
      const ordered = this.packingCompanies.reduce((sum, company) => sum + company.ordered, 0);
      const taken = this.packingCompanies.reduce((sum, company) => sum + company.taken, 0);
      return { ordered, taken, percent: ordered ? Math.min(100, Math.round((taken / ordered) * 100)) : 0 };
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        {
          key: "taken",
          label: this.$t("taken"),
          value: this.summary.taken,
          of: this.summary.ordered,
          percent: this.summary.percent,
          tone: "inverse",
        },
        { key: "companies", label: this.$t("companies"), value: this.packingCompanies.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.packingCompanies.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    /** Zbirni red firme: tekst po smenama i ukupno. */
    companyTotals(company) {
      const totals = { total: this.formatQuantity(company.totals, "total") };
      this.visibleShifts.forEach((shift) => {
        totals[shift.key] = this.formatQuantity(company.totals, shift.key);
      });
      return totals;
    },
    matches(row, fields, search) {
      return this.customSearch([row], fields.map((value) => ({ value })), search).length > 0;
    },
  },
};
</script>
